// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// "Ground" — the geography of the return.
//
// Three linked pieces, one question: does the ground you farm decide what the
// farming earns, and does it decide what the land adds on top?
//
//   1. a choropleth of the 32 ABARES survey regions,
//   2. a ranked strip of the same 32 regions (the map cannot be read by name,
//      and a phone-sized map cannot be read at all without one),
//   3. a per-year count of regions whose land value FELL — the counterweight,
//      on the same page as the headline rather than in a footnote.
//
// NO DESIGN SPEC WAS SUPPLIED for this view (the brief arrived with the spec
// field literally `undefined`), so the composition below is my own; every
// house rule in the brief is still obeyed literally.
//
// ONE THING IN THE BRIEF WAS DELIBERATELY DROPPED. I had planned a fourth
// panel: a zone dumbbell showing the site's zone finding (High Rainfall earns
// ~0% from farming while its land appreciates at much the same rate as
// everywhere else). It cannot be built honestly here. This view is only allowed
// to average tier<=2 settled years, and on that subset only 3 of the 8 High
// Rainfall regions have a paired firm-or-fair estimate for BOTH rate series —
// a zone mean built on 3 of 8 regions is a different, weaker claim wearing the
// same clothes. The zone comparison therefore lives here as a FILTER on the
// map (grouping, not a measure), and the zone means stay on the view that can
// afford them.

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { loadCore, loadMeta, loadRegions } from '../data';
import type { Core, Region, RegionYear, Zone } from '../types';
import {
  CRIMSON, FARMING, GRAPHITE, LAND, NEUTRAL, SCOPE_SHORT, ZONE_COLOUR, ZONE_ORDER, returnColour,
} from '../theme';
import {
  collapse, evidenceLines, isOutlineOnly, isTokenSized, tierOpacity, tierPrefix, type Tier,
} from '../uncertainty';
import {
  axisBottom, chartSvg, el, finite, hatchDefs, linearScale, markTip,
} from '../components/svg';
import { esc, fy, num, signedPct } from '../format';
import { glossIcon, glossTerm } from '../components/glossary';
import { openRegion } from '../components/regionDrawer';
import { currentRoute, setParams } from '../router';

/* ── the shapes core.json actually carries ────────────────────────────────────
 * src/types.ts still describes the earlier `rel: Reliability` cut of the data.
 * The published files carry the five-tier system instead (`tier` on every
 * region-year, `apprTier` alongside it for the appreciation half). Narrowing
 * here rather than editing types.ts keeps this view to the one file it is
 * allowed to write. */
interface RY extends RegionYear { tier: Tier; apprTier: Tier }
interface Reg extends Region { tier: Tier; series: RY[] }

type Measure = 'ex' | 'inc';
type Mode = 'mean' | 'year';

/**
 * How many settled firm-or-fair years a region needs before this view will
 * print an average for it.
 *
 * Without a floor, "only tier<=2 inputs" quietly produces a mean of ONE year
 * for NT Alice Springs and of five unrepresentative years for WA Pilbara
 * (which then lands at -11.1%/yr and takes the bottom of the ranking on the
 * strength of five observations out of 35). A rule that admits a one-year mean
 * is not an uncertainty rule.
 */
const MIN_MEAN_YEARS = 10;

const MEASURE_LABEL: Record<Measure, string> = {
  ex: 'What the farming earned',
  inc: 'Farming plus what the land added',
};
const MEASURE_SHORT: Record<Measure, string> = {
  ex: 'farming only',
  inc: 'land counted too',
};

export async function renderGround(host: HTMLElement): Promise<void> {
  const [core, meta, geo] = await Promise.all([loadCore(), loadMeta(), loadRegions()]);
  const regions = (core as Core).regions as unknown as Reg[];
  const byName = new Map<string, Reg>(regions.map((r) => [r.name, r]));
  const provisional = core.provisionalYear;
  const settledLast = core.settledYears[core.settledYears.length - 1];

  /* ── state, seeded from the URL so a shared link lands on the same map ──── */
  const params = currentRoute().params;
  const state = {
    measure: (params.get('measure') === 'inc' ? 'inc' : 'ex') as Measure,
    mode: (params.get('mode') === 'year' ? 'year' : 'mean') as Mode,
    year: clampYear(Number(params.get('year')), core.years, settledLast),
    zone: readZone(params.get('zone')),
  };

  host.innerHTML = '';

  /* ─────────────────────────── head ─────────────────────────── */
  const head = document.createElement('div');
  head.className = 'view-head';
  head.innerHTML = `
    <h1>Where farming pays</h1>
    <p class="lede">Thirty-two ${glossTerm('broadacre')} survey regions, coloured by the
      ${glossTerm('rateOfReturn', 'rate of return')} to farm capital. Switch between counting only
      what the farming earned and adding what the land appreciated, and the map changes shape:
      the second one is far less about where you are.</p>
    <p>Every average on this page uses a region's own firm-or-fair years only, and never the
      provisional ${provisional == null ? '' : fy(provisional)} year. Where the survey cannot
      support a figure the region is left grey rather than shaded a plausible colour.</p>`;
  host.append(head);

  /* ─────────────────────────── controls ─────────────────────────── */
  const controls = document.createElement('div');
  controls.className = 'controls';

  const measureCtl = segmented(
    'Colour the map by',
    [
      { key: 'ex', label: MEASURE_LABEL.ex },
      { key: 'inc', label: MEASURE_LABEL.inc },
    ],
    () => state.measure,
    (k) => { state.measure = k as Measure; sync(); },
  );

  const modeCtl = segmented(
    'Over',
    [
      { key: 'mean', label: 'The long run' },
      { key: 'year', label: 'One year' },
    ],
    () => state.mode,
    (k) => { state.mode = k as Mode; sync(); },
  );

  const yearCtl = document.createElement('div');
  yearCtl.className = 'control';
  const yearLabel = document.createElement('label');
  yearLabel.className = 'field-label';
  yearLabel.htmlFor = 'ground-year';
  const yearInput = document.createElement('input');
  yearInput.type = 'range';
  yearInput.id = 'ground-year';
  yearInput.min = String(core.years[0]);
  yearInput.max = String(core.years[core.years.length - 1]);
  yearInput.step = '1';
  yearInput.value = String(state.year);
  yearInput.addEventListener('input', () => {
    state.year = clampYear(Number(yearInput.value), core.years, settledLast);
    sync();
  });
  yearCtl.append(yearLabel, yearInput);

  const zoneCtl = document.createElement('div');
  zoneCtl.className = 'control';
  const zoneLab = document.createElement('span');
  zoneLab.className = 'field-label';
  zoneLab.innerHTML = `Agro-climatic ${glossTerm('zone', 'zone')}`;
  const zoneRow = document.createElement('div');
  zoneRow.className = 'row';
  zoneRow.style.flexWrap = 'wrap';
  zoneRow.append(zoneChip(null, 'All 32 regions'));
  for (const z of ZONE_ORDER) zoneRow.append(zoneChip(z as Zone, z));
  zoneCtl.append(zoneLab, zoneRow);

  controls.append(measureCtl.node, modeCtl.node, yearCtl, zoneCtl);
  host.append(controls);

  function zoneChip(z: Zone | null, label: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn-sm';
    b.dataset.zone = z ?? '';
    const count = z == null ? regions.length : regions.filter((r) => r.zone === z).length;
    // The count inherits the button's own colour rather than taking `.muted`:
    // on the pressed (dark) state a fixed grey drops under 3:1.
    b.innerHTML = `${z ? `<span class="legend-swatch" style="background:${ZONE_COLOUR[z]}"></span> ` : ''}${esc(label)} <span style="opacity:.72">${count}</span>`;
    b.style.display = 'inline-flex';
    b.style.alignItems = 'center';
    b.style.gap = '6px';
    b.addEventListener('click', () => {
      // Clicking the zone you are already in clears the filter — never a dead click.
      state.zone = state.zone === z ? null : z;
      sync();
      fitMap();
    });
    return b;
  }

  /* ─────────────────────────── the map ─────────────────────────── */
  const mapCard = document.createElement('section');
  mapCard.className = 'card stack';
  const mapHead = document.createElement('div');
  mapHead.innerHTML = `
    <h2 class="card-title" id="ground-map-title">Where the return comes from</h2>
    <p class="card-sub" id="ground-map-sub"></p>`;
  const mapFrame = document.createElement('div');
  mapFrame.className = 'map-frame';
  const mapLegend = document.createElement('div');
  mapLegend.className = 'stack';
  mapLegend.style.gap = '6px';
  const mapNote = document.createElement('p');
  mapNote.className = 'note';
  mapCard.append(mapHead, mapFrame, mapLegend, mapNote);
  host.append(mapCard);

  /* ─────────────────────────── the ranked strip ─────────────────────────── */
  const stripCard = document.createElement('section');
  stripCard.className = 'card stack';
  const stripHead = document.createElement('div');
  stripHead.innerHTML = `
    <h2 class="card-title" id="ground-strip-title">The same 32 regions, in order</h2>
    <p class="card-sub" id="ground-strip-sub"></p>`;
  const stripHost = document.createElement('div');
  const stripNote = document.createElement('p');
  stripNote.className = 'note';
  stripCard.append(stripHead, stripHost, stripNote);
  host.append(stripCard);

  /* ─────────────────────────── the counterweight ─────────────────────────── */
  const fallCard = document.createElement('section');
  fallCard.className = 'card stack';
  fallCard.innerHTML = `
    <div>
      <h2 class="card-title">Land does not only go up</h2>
      <p class="card-sub">One column per year. Each column counts the survey regions whose
        ${glossTerm('capitalAppreciation', 'capital appreciation')} was negative that year — the
        land was worth less at the end of the year than at the start — against those where it rose.
        Grey is the honest remainder: regions where the survey cannot support a firm or fair
        figure either way. Click a column to put that year on the map.</p>
    </div>`;
  const fallHost = document.createElement('div');
  const fallNote = document.createElement('p');
  fallNote.className = 'note';
  fallCard.append(fallHost, fallNote);
  host.append(fallCard);

  /* ─────────────────────────── footer notes ─────────────────────────── */
  const foot = document.createElement('div');
  foot.className = 'stack';
  foot.innerHTML = `
    <p class="small muted">Regions are ABARES' own AAGIS survey regions and the three
      agro-climatic zones come from the same boundary file — nothing here is redrawn.
      ${glossIcon('aagis')}</p>
    <p class="small muted">Both rate-of-return series are used exactly as ABARES publishes
      them. This site never recalculates a return as profit divided by capital: that method
      fails to reproduce the published series in a fifth to a half of region-years, depending
      on the region. ${glossIcon('rateOfReturn')}</p>
    <p class="xsmall muted">${esc(SCOPE_SHORT)}</p>`;
  host.append(foot);

  /* ══════════════════════════ Leaflet ══════════════════════════ */
  const map = L.map(mapFrame, {
    // The map lives inside a scrolling page, so the wheel belongs to the page.
    scrollWheelZoom: false,
    zoomControl: true,
    attributionControl: true,
  });
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
    attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  }).addTo(map);

  const layerByName = new Map<string, L.Path>();
  const gj = L.geoJSON(geo, {
    style: () => ({ weight: 0.8, color: '#ffffff', fillOpacity: 0.85 }),
    onEachFeature: (feature, layer) => {
      const name = String((feature.properties as Record<string, unknown> | undefined)?.name ?? '');
      const reg = byName.get(name);
      if (!reg) return; // the join is 32/32; a miss would be a data change, not a design case
      layerByName.set(name, layer as L.Path);
      layer.on('click', () => openDossier(reg));
      layer.on('mouseover', () => highlight(name));
      layer.on('mouseout', () => highlight(null));
    },
  }).addTo(map);

  // Keyboard parity for the polygons. Leaflet renders each one as a plain
  // <path> with no tab stop, so a keyboard user would otherwise have the strip
  // below as their only route into a region.
  for (const [name, layer] of layerByName) {
    const node = layer.getElement() as SVGElement | null;
    if (!node) continue;
    node.setAttribute('tabindex', '0');
    node.setAttribute('role', 'button');
    node.addEventListener('keydown', (e) => {
      const ev = e as KeyboardEvent;
      if (ev.key !== 'Enter' && ev.key !== ' ') return;
      ev.preventDefault();
      const reg = byName.get(name);
      if (reg) openDossier(reg);
    });
    node.addEventListener('focus', () => highlight(name));
    node.addEventListener('blur', () => highlight(null));
  }

  // A single requestAnimationFrame fires while the container is still being
  // laid out and the map ends up clipped; the observer catches every later
  // reflow too (drawer open, orientation change, font swap).
  //
  // AND the first fit has to wait for it. `fitBounds` against a container that
  // is still 0px wide computes a zoom for a 0px viewport and lands somewhere
  // inside a single region — the map came up as one flat teal rectangle until
  // the fit was deferred to the first real measurement.
  let fitted = false;
  const ro = new ResizeObserver(() => {
    map.invalidateSize();
    if (!fitted && mapFrame.clientWidth > 0 && mapFrame.clientHeight > 0) {
      fitted = true;
      fitMap();
    }
  });
  ro.observe(mapFrame);
  if (mapFrame.clientWidth > 0 && mapFrame.clientHeight > 0) {
    fitted = true;
    fitMap();
  }

  // The only listener this view puts on window. It exists to tear Leaflet's own
  // window listeners down when the router swaps the view out from under us.
  const onHash = () => {
    if (host.isConnected) return;
    ro.disconnect();
    map.remove();
    removeEventListener('hashchange', onHash);
  };
  addEventListener('hashchange', onHash);

  function fitMap(): void {
    const bounds = state.zone == null
      ? gj.getBounds()
      : boundsForZone(state.zone);
    if (bounds && bounds.isValid()) map.fitBounds(bounds, { padding: [12, 12] });
  }

  function boundsForZone(z: Zone): L.LatLngBounds | null {
    let b: L.LatLngBounds | null = null;
    for (const [name, layer] of layerByName) {
      if (byName.get(name)?.zone !== z) continue;
      const lb = (layer as L.Polygon).getBounds();
      b = b == null ? L.latLngBounds(lb.getSouthWest(), lb.getNorthEast()) : b.extend(lb);
    }
    return b;
  }

  let highlighted: string | null = null;
  function highlight(name: string | null): void {
    if (highlighted === name) return;
    highlighted = name;
    for (const [n, layer] of layerByName) {
      layer.setStyle(mapStyle(n, n === name));
      if (n === name) layer.bringToFront();
    }
    for (const row of stripHost.querySelectorAll<SVGGElement>('g[data-region]')) {
      row.classList.toggle('sel', row.dataset.region === name);
    }
  }

  /* ══════════════════════════ measurement ══════════════════════════ */

  interface Cell {
    value: number | null;
    tier: Tier;
    sample: number | null;
    rse: number | null;
    /** Extra tooltip lines that explain an average rather than a single year. */
    basis: string | null;
    /** How many years went into an average (null for a single year). */
    used: number | null;
  }

  function yearCell(ry: RY, m: Measure): Cell {
    if (m === 'ex') {
      return { value: ry.rateEx, tier: ry.tier, sample: ry.sample, rse: ry.rateExRse, basis: null, used: null };
    }
    // Including appreciation splices two survey estimates together, so the
    // result can be no firmer than the weaker of the two.
    const t = Math.max(ry.tier, ry.apprTier) as Tier;
    return { value: ry.rateInc, tier: t, sample: ry.sample, rse: ry.rateIncRse, basis: null, used: null };
  }

  function meanCell(r: Reg, m: Measure): Cell {
    let sum = 0; let used = 0; let dropped = 0; let worst: Tier = 1;
    for (const s of r.series) {
      // The provisional year is excluded from every mean, ranking and trend.
      if (provisional != null && s.year === provisional) continue;
      const c = yearCell(s, m);
      if (c.value != null && Number.isFinite(c.value) && c.tier <= 2) {
        sum += c.value; used++;
        if (c.tier > worst) worst = c.tier;
      } else dropped++;
    }
    const basis = `${used} of the region's ${used + dropped} settled years carry a firm or fair estimate`;
    if (used < MIN_MEAN_YEARS) {
      return { value: null, tier: 5, sample: r.medianSample, rse: null, basis, used };
    }
    return { value: sum / used, tier: worst, sample: r.medianSample, rse: null, basis, used };
  }

  function cellFor(r: Reg, m: Measure = state.measure): Cell {
    if (state.mode === 'mean') return meanCell(r, m);
    const ry = r.series.find((s) => s.year === state.year);
    // A region with no row at all this year is not a zero and not an estimate —
    // the three NT regions genuinely do not exist in the survey before 1992.
    if (!ry) {
      return {
        value: null, tier: 5, sample: null, rse: null, used: null,
        basis: `Not surveyed in ${fy(state.year)} — this region enters the survey in ${fy(r.firstYear)}`,
      };
    }
    return yearCell(ry, m);
  }

  function visible(r: Reg): boolean {
    return state.zone == null || r.zone === state.zone;
  }

  /** The zero-anchored cap for the colour ramp, from the values on screen. */
  function colourCap(): number {
    let maxAbs = 0;
    for (const r of regions) {
      if (!visible(r)) continue;
      const c = cellFor(r);
      if (c.value == null || c.tier > 3) continue;
      maxAbs = Math.max(maxAbs, Math.abs(c.value));
    }
    const steps = [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50, 60, 80];
    return steps.find((s) => s >= maxAbs) ?? Math.ceil(maxAbs / 10) * 10;
  }

  let cap = colourCap();

  function hueFor(m: Measure): string {
    return m === 'ex' ? FARMING : LAND;
  }

  function mapStyle(name: string, isHot = false): L.PathOptions {
    const r = byName.get(name);
    const base: L.PathOptions = {
      weight: isHot ? 3 : 0.8,
      color: isHot ? GRAPHITE : '#ffffff',
      fillOpacity: 0.85,
      opacity: 1,
    };
    if (!r) return { ...base, fillColor: NEUTRAL, fillOpacity: 0.25 };
    if (!visible(r)) {
      // Filtered out, not missing: keep it on the map, drained of meaning.
      return { ...base, fillColor: NEUTRAL, fillOpacity: 0.12, opacity: 0.45, weight: 0.6 };
    }
    const c = cellFor(r);
    if (c.value == null || c.tier >= 5) {
      return { ...base, fillColor: NEUTRAL, fillOpacity: 0.3, dashArray: '3 3' };
    }
    if (isTokenSized(c.tier)) {
      // Tier 4 never encodes magnitude, so it gets no position on the ramp at all.
      return { ...base, fillColor: NEUTRAL, fillOpacity: 0.45, dashArray: '2 3' };
    }
    const fill = returnColour(c.value, cap, hueFor(state.measure));
    if (isOutlineOnly(c.tier)) {
      // Tier 3: direction, not magnitude — the outline carries the sign.
      return { ...base, fillColor: fill, fillOpacity: 0, color: isHot ? GRAPHITE : fill, weight: isHot ? 3 : 2.2 };
    }
    return { ...base, fillColor: fill, fillOpacity: 0.85 * tierOpacity(c.tier) + 0.1 };
  }

  function mapTooltip(r: Reg): string {
    const c = cellFor(r);
    const lines: string[] = [];
    const measure = MEASURE_SHORT[state.measure];
    if (c.value == null) {
      lines.push(`No figure shown — ${measure}`);
      if (c.basis) lines.push(c.basis);
      else lines.push('The survey cannot support a figure here.');
    } else {
      const v = `${tierPrefix(c.tier)}${signedPct(c.value)}`;
      lines.push(state.mode === 'mean'
        ? `${v} a year — ${measure}, average of ${num(c.used)} firm-or-fair years`
        : `${v} — ${measure}, ${fy(state.year)}`);
      if (state.mode === 'mean') {
        if (c.basis) lines.push(c.basis);
      } else {
        for (const l of evidenceLines(c.sample, c.tier, c.rse)) lines.push(l);
      }
    }
    lines.push(`${r.zone} zone${r.state ? ` · ${r.state}` : ''}`);
    lines.push('Click for this region in full');
    return `<strong>${esc(r.name)}</strong><br>${lines.map((l) => esc(l)).join('<br>')}`;
  }

  /* ══════════════════════════ the ranked strip ══════════════════════════ */

  function drawStrip(): void {
    stripHost.innerHTML = '';

    const rows = regions.filter(visible).map((r) => ({ r, c: cellFor(r) }));
    const ranked = rows.filter((x) => x.c.value != null && x.c.tier <= 2)
      .sort((a, b) => (b.c.value ?? 0) - (a.c.value ?? 0));
    const rest = rows.filter((x) => !(x.c.value != null && x.c.tier <= 2))
      .sort((a, b) => a.r.name.localeCompare(b.r.name));

    const W = 580;
    const LEFT = 196;
    const RIGHT = 528;
    const TOP = 26;
    const ROW = 22;
    const divider = rest.length ? 26 : 0;
    const H = TOP + rows.length * ROW + divider + 46;

    const values = ranked.map((x) => x.c.value ?? 0);
    const lo = Math.min(0, ...values);
    const hi = Math.max(0, ...values, 1);
    const x = linearScale([lo, hi], [LEFT, RIGHT]);
    const x0 = finite(x(0), LEFT);

    const label = state.mode === 'mean'
      ? `Regions ranked by ${MEASURE_SHORT[state.measure]} rate of return, long-run average`
      : `Regions ranked by ${MEASURE_SHORT[state.measure]} rate of return in ${fy(state.year)}`;
    const svg = chartSvg(W, H, label);

    // zero rule, drawn once and labelled — it is the whole reading of this chart
    svg.append(el('line', {
      class: 'zero-line', x1: x0, y1: TOP - 6, x2: x0, y2: TOP + rows.length * ROW + divider + 4,
    }));
    const zeroLab = el('text', { class: 'axis-text', x: x0, y: TOP - 12, 'text-anchor': 'middle' });
    zeroLab.textContent = '0% — farming paid for its own capital';
    svg.append(zeroLab);

    let y = TOP;
    const hue = hueFor(state.measure);

    const drawRow = (r: Reg, c: Cell, rank: number | null) => {
      const g = el('g', { class: 'mark', tabindex: '0', role: 'button' });
      g.dataset.region = r.name;

      // zone swatch: a GROUPING, never a measure — hence the desaturated tints
      g.append(el('rect', {
        x: 2, y: y + 4, width: 8, height: 12, rx: 2,
        fill: ZONE_COLOUR[r.zone] ?? NEUTRAL, opacity: 0.9,
      }));

      const nameText = el('text', {
        class: 'axis-text', x: LEFT - 8, y: y + 14, 'text-anchor': 'end',
        fill: 'var(--text-secondary)',
      });
      nameText.textContent = `${rank == null ? '' : `${rank}. `}${shorten(r.name, 30)}`;
      g.append(nameText);

      if (c.value == null) {
        // No estimate: a fixed token on the zero rule. Nothing about it is a size.
        g.append(el('rect', {
          x: x0 + 2, y: y + 6, width: 9, height: 9, rx: 2,
          fill: NEUTRAL, opacity: 0.5,
        }));
        const t = el('text', { class: 'axis-text', x: x0 + 16, y: y + 14, fill: 'var(--text-tertiary)' });
        t.textContent = 'no figure';
        g.append(t);
      } else if (isTokenSized(c.tier)) {
        g.append(el('rect', {
          x: x0 + 2, y: y + 6, width: 9, height: 9, rx: 2, fill: NEUTRAL, opacity: 0.6,
        }));
        const t = el('text', { class: 'axis-text', x: x0 + 16, y: y + 14, fill: 'var(--text-tertiary)' });
        t.textContent = 'no basis for a figure';
        g.append(t);
      } else {
        const v = c.value;
        const xv = finite(x(v), x0);
        const bx = Math.min(x0, xv);
        const bw = Math.max(1.5, Math.abs(xv - x0));
        const colour = v < 0 ? CRIMSON : hue;
        const rect = el('rect', {
          x: bx, y: y + 5, width: bw, height: 12, rx: 1.5,
        });
        if (isOutlineOnly(c.tier)) {
          // Tier 3 shows sign and rough direction, not a magnitude to be read off.
          rect.setAttribute('fill', 'none');
          rect.setAttribute('stroke', collapse(colour, c.tier));
          rect.setAttribute('stroke-width', '1.4');
          rect.setAttribute('stroke-dasharray', '3 2');
        } else {
          rect.setAttribute('fill', collapse(colour, c.tier));
          rect.setAttribute('opacity', String(tierOpacity(c.tier)));
        }
        g.append(rect);
        const t = el('text', {
          class: 'axis-text', x: (v < 0 ? bx - 6 : bx + bw + 6), y: y + 14,
          'text-anchor': v < 0 ? 'end' : 'start', fill: 'var(--text-secondary)',
        });
        t.textContent = `${tierPrefix(c.tier)}${signedPct(c.value)}`;
        g.append(t);
      }

      markTip(g, [
        r.name,
        c.value == null
          ? `No figure — ${MEASURE_SHORT[state.measure]}`
          : state.mode === 'mean'
            ? `${tierPrefix(c.tier)}${signedPct(c.value)} a year — ${MEASURE_SHORT[state.measure]}, long-run average`
            : `${tierPrefix(c.tier)}${signedPct(c.value)} — ${MEASURE_SHORT[state.measure]}, ${fy(state.year)}`,
        state.mode === 'mean' ? c.basis : evidenceLines(c.sample, c.tier, c.rse).join('\n'),
        state.mode === 'year' && c.basis ? c.basis : null,
        `${r.zone} zone${r.state ? ` · ${r.state}` : ''}`,
        'Click for this region in full',
      ]);

      // From the strip the map is the thing that needs to move: the reader
      // picked a NAME and has no idea where it is.
      const open = () => openDossier(r, true);
      g.addEventListener('click', open);
      g.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        open();
      });
      g.addEventListener('mouseenter', () => highlight(r.name));
      g.addEventListener('mouseleave', () => highlight(null));
      g.addEventListener('focus', () => highlight(r.name));
      g.addEventListener('blur', () => highlight(null));

      svg.append(g);
      y += ROW;
    };

    ranked.forEach((row, i) => drawRow(row.r, row.c, i + 1));

    if (rest.length) {
      y += 6;
      const t = el('text', { class: 'axis-text', x: 2, y: y + 10, fill: 'var(--text-tertiary)' });
      t.textContent = `Not ranked — ${rest.length} region${rest.length === 1 ? '' : 's'} the survey cannot rank here`;
      svg.append(t);
      svg.append(el('line', {
        class: 'grid-line', x1: 2, y1: y + 16, x2: RIGHT, y2: y + 16,
      }));
      y += 20;
      rest.forEach((row) => drawRow(row.r, row.c, null));
    }

    axisBottom(svg, x, H - 34, {
      format: (v) => `${v > 0 ? '+' : ''}${v}%`,
      title: state.mode === 'mean' ? 'rate of return, % a year (long-run average)' : `rate of return, % in ${fy(state.year)}`,
      x0: LEFT, x1: RIGHT,
    });

    const scroll = document.createElement('div');
    scroll.className = 'chart-scroll';
    const inner = document.createElement('div');
    inner.style.minWidth = `${W}px`;
    svg.style.width = `${W}px`;
    inner.append(svg);
    scroll.append(inner);
    stripHost.append(scroll);

    const legend = document.createElement('div');
    legend.className = 'legend';
    legend.innerHTML = [
      swatch(hue, state.measure === 'ex' ? 'positive: what the farming earned' : 'positive: farming plus what the land added'),
      swatch(CRIMSON, 'below zero'),
      `<span class="legend-item"><span class="legend-swatch" style="border:1.4px dashed ${CRIMSON};background:none"></span> outline only: a weak estimate — read the direction, not the number</span>`,
      swatch(NEUTRAL, 'no figure the survey can support'),
    ].join('');
    stripHost.append(legend);

    const sub = stripCard.querySelector('#ground-strip-sub');
    if (sub) {
      sub.textContent = state.mode === 'mean'
        ? `Each bar is one region's own average across its firm-or-fair settled years — never a straight 36-year average, and never including ${provisional == null ? 'the provisional year' : fy(provisional)}. Hover a bar to find the region on the map; click for the full region.`
        : `Each bar is that region's published rate of return in ${fy(state.year)}. Hover a bar to find the region on the map; click for the full region.`;
    }

    stripNote.innerHTML = stripNoteHtml(ranked.length, rest.length, rows.length);
  }

  function stripNoteHtml(rankedN: number, restN: number, total: number): string {
    if (state.mode === 'mean') {
      const dropped = regions.filter(visible)
        .reduce((n, r) => n + countDropped(r, state.measure), 0);
      return `<strong>What was left out.</strong> ${rankedN} of ${total} regions are ranked.
        ${restN} are not: each has fewer than ${MIN_MEAN_YEARS} settled years with a firm or fair
        estimate, and an average of five unrepresentative years is not an average.
        Across the regions shown, ${num(dropped)} settled region-years were dropped from these
        averages as weak or unsupported, and every region's provisional
        ${provisional == null ? 'year' : fy(provisional)} was dropped as well.
        That is why an average here can sit a little above or below the whole-series average in
        the region's own dossier: this page averages the years the survey can stand behind,
        not all of them.`;
    }
    const dropped = regions.filter(visible).filter((r) => {
      const c = cellFor(r);
      return !(c.value != null && c.tier <= 2);
    }).length;
    return `<strong>What was left out.</strong> ${rankedN} of ${total} regions are ranked for
      ${fy(state.year)}. ${dropped} are not — the survey's estimate for them that year is weak,
      unsupported, or absent, and a ranking built on it would be a ranking of noise.`;
  }

  function countDropped(r: Reg, m: Measure): number {
    let n = 0;
    for (const s of r.series) {
      if (provisional != null && s.year === provisional) continue;
      const c = yearCell(s, m);
      if (!(c.value != null && c.tier <= 2)) n++;
    }
    return n;
  }

  /* ══════════════════════ the counterweight chart ══════════════════════ */

  interface FallYear { year: number; fell: number; rose: number; unknown: number; total: number }

  const fallByYear: FallYear[] = core.years.map((yr) => {
    let fell = 0; let rose = 0; let unknown = 0; let total = 0;
    for (const r of regions) {
      const s = r.series.find((q) => q.year === yr);
      if (!s) continue; // three NT regions do not exist before 1992 — not a zero
      total++;
      // A count is a cumulative total, so it takes tier<=2 inputs only.
      if (s.apprTier <= 2 && s.appreciation != null && Number.isFinite(s.appreciation)) {
        if (s.appreciation < 0) fell++; else rose++;
      } else unknown++;
    }
    return { year: yr, fell, rose, unknown, total };
  });

  function drawFall(): void {
    fallHost.innerHTML = '';
    const W = 600; const H = 264;
    const LEFT = 34; const RIGHT = W - 12; const TOP = 14; const BOT = H - 46;
    const svg = chartSvg(W, H, 'Regions whose land value fell, by year');
    svg.append(hatchDefs('ground-fall-hatch', GRAPHITE, 0.55));

    const maxN = Math.max(...fallByYear.map((f) => f.total), 1);
    const y = linearScale([0, maxN], [BOT, TOP]);
    const bw = (RIGHT - LEFT) / fallByYear.length;
    const xOf = (i: number) => LEFT + i * bw;

    // y grid
    for (const t of [0, 8, 16, 24, 32].filter((v) => v <= maxN)) {
      const yy = finite(y(t), BOT);
      svg.append(el('line', { class: t === 0 ? 'axis-line' : 'grid-line', x1: LEFT, y1: yy, x2: RIGHT, y2: yy }));
      const lab = el('text', { class: 'axis-text', x: LEFT - 6, y: yy + 4, 'text-anchor': 'end' });
      lab.textContent = String(t);
      svg.append(lab);
    }

    fallByYear.forEach((f, i) => {
      const x = xOf(i) + 1.5;
      const w = Math.max(2, bw - 3);
      const isProv = provisional != null && f.year === provisional;
      let acc = 0;

      // One group per column. The click target is the GROUP, not a transparent
      // rect laid over the segments — an overlay rect would sit on top and eat
      // every segment's own tooltip, which is the classic way a stacked column
      // ends up with one tooltip for three different facts.
      const col = el('g', { class: 'mark', tabindex: '0', role: 'button' });
      col.append(el('rect', {
        x: xOf(i), y: TOP, width: bw, height: BOT - TOP, fill: 'transparent',
      }));
      const svgAppend = (n: SVGElement) => col.append(n);
      const seg = (n: number, colour: string, opacity: number, what: string, extra: string[]) => {
        if (n <= 0) return;
        const y1 = finite(y(acc), BOT);
        const y2 = finite(y(acc + n), BOT);
        const rect = el('rect', {
          class: 'mark', x, y: Math.min(y1, y2), width: w, height: Math.max(1, Math.abs(y1 - y2)),
          fill: colour, opacity,
        });
        markTip(rect, [
          `${fy(f.year)}: ${n} of ${f.total} survey regions — ${what}`,
          ...extra,
          isProv ? 'Provisional year — ABARES will revise it. It is excluded from every average and trend on this site.' : null,
          'Click to put this year on the map',
        ]);
        svgAppend(rect);
        acc += n;
      };
      seg(f.fell, CRIMSON, 0.9, 'the land was worth LESS at the end of the year', [
        `${f.rose} rose, ${f.unknown} had no firm or fair estimate`,
      ]);
      seg(f.rose, LAND, 0.85, 'the land appreciated', [
        `${f.fell} fell, ${f.unknown} had no firm or fair estimate`,
      ]);
      seg(f.unknown, NEUTRAL, 0.4, 'no firm or fair estimate either way', [
        'These are not zeroes and they are not "no change" — the survey simply cannot say.',
      ]);

      if (isProv) {
        // The provisional year is hatched everywhere it appears.
        svgAppend(el('rect', {
          x, y: finite(y(f.total), TOP), width: w, height: Math.max(1, BOT - finite(y(f.total), TOP)),
          fill: 'url(#ground-fall-hatch)', opacity: 0.9, 'pointer-events': 'none',
        }));
      }

      // the year the map is currently showing, marked on the timeline
      if (state.mode === 'year' && state.year === f.year) {
        svgAppend(el('rect', {
          x: xOf(i), y: TOP, width: bw, height: BOT - TOP,
          fill: 'none', stroke: GRAPHITE, 'stroke-width': 1.6, 'pointer-events': 'none',
        }));
      }

      col.setAttribute('aria-label', `${fy(f.year)}: land value fell on ${f.fell} of the ${f.total} survey regions. Activate to show ${fy(f.year)} on the map.`);
      const go = () => {
        state.mode = 'year';
        state.year = f.year;
        sync();
        mapFrame.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };
      col.addEventListener('click', go);
      col.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key !== 'Enter' && ev.key !== ' ') return;
        ev.preventDefault();
        go();
      });
      svg.append(col);
    });

    axisBottom(svg, linearScale([0, fallByYear.length - 1], [LEFT + bw / 2, RIGHT - bw / 2]), BOT, {
      ticks: fallByYear.map((_, i) => i).filter((i) => i % 5 === 0),
      format: (i) => fy(fallByYear[Math.round(i)]?.year),
      title: 'financial year',
    });

    const scroll = document.createElement('div');
    scroll.className = 'chart-scroll';
    const inner = document.createElement('div');
    inner.style.minWidth = `${W}px`;
    svg.style.width = `${W}px`;
    inner.append(svg);
    scroll.append(inner);
    fallHost.append(scroll);

    const legend = document.createElement('div');
    legend.className = 'legend';
    legend.innerHTML = [
      swatch(CRIMSON, 'land value fell that year'),
      swatch(LAND, 'land appreciated'),
      swatch(NEUTRAL, 'no firm or fair estimate'),
      `<span class="legend-item"><span class="legend-swatch" style="background:repeating-linear-gradient(45deg,${GRAPHITE},${GRAPHITE} 2px,transparent 2px,transparent 5px)"></span> provisional year</span>`,
    ].join('');
    fallHost.append(legend);
  }

  const fellSomewhere = fallByYear.filter((f) => f.fell > 0 && f.year !== provisional);
  const fellTotal = fallByYear.reduce((n, f) => n + (f.year === provisional ? 0 : f.fell), 0);
  const judgedTotal = fallByYear.reduce((n, f) => n + (f.year === provisional ? 0 : f.fell + f.rose), 0);
  fallNote.innerHTML = `<strong>The counterweight.</strong> Land appreciation is the larger half of
    the return over 36 years, and it is not a one-way street: across the settled years,
    ${num(fellTotal)} of the ${num(judgedTotal)} region-years with a firm or fair estimate had land
    values going backwards, in ${fellSomewhere.length} separate years — clustered in the early
    1990s and again around 2010–13. A paper gain is real wealth and real collateral, but it is not
    cash, it is only realised on sale, and it can be taken away again.`;

  /* ══════════════════════════ the region dossier ══════════════════════════ */

  /**
   * Drill down into a region.
   *
   * The dossier is the SHARED component every view drills into, never a
   * map-flavoured copy of it — four slightly different dossiers is how one
   * region ends up with four different numbers. All this view adds is putting
   * the region under the reader's eye on the map first, so that when they close
   * the drawer they are looking at the ground they just read about.
   */
  function openDossier(r: Reg, panTo = false): void {
    if (panTo) {
      const layer = layerByName.get(r.name) as L.Polygon | undefined;
      if (layer) {
        map.fitBounds(layer.getBounds(), { padding: [24, 24] });
        highlight(r.name);
      }
    }
    openRegion(r.name);
  }

  /* ══════════════════════════ redraw ══════════════════════════ */

  function sync(): void {
    cap = colourCap();

    measureCtl.update();
    modeCtl.update();
    yearCtl.hidden = state.mode !== 'year';
    yearLabel.textContent = `Year — ${fy(state.year)}${provisional != null && state.year === provisional ? ' (provisional)' : ''}`;
    yearInput.value = String(state.year);
    for (const b of zoneRow.querySelectorAll<HTMLButtonElement>('button')) {
      b.setAttribute('aria-pressed', String((b.dataset.zone || null) === state.zone));
    }

    for (const [name, layer] of layerByName) {
      layer.setStyle(mapStyle(name, name === highlighted));
      const reg = byName.get(name);
      if (reg) layer.bindTooltip(mapTooltip(reg), { sticky: true, direction: 'top' });
      // Leaflet's own tooltip is the hover surface here; the global [data-tip]
      // tooltip is deliberately NOT attached to these paths, because two
      // tooltips on one mark is worse than either alone. The path still gets an
      // accessible name.
      const node = layer.getElement() as SVGElement | null;
      if (node && reg) node.setAttribute('aria-label', mapTooltip(reg).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim());
    }

    const sub = mapCard.querySelector('#ground-map-sub');
    if (sub) {
      sub.textContent = state.mode === 'mean'
        ? `Each region is shaded by its own average rate of return across its firm-or-fair settled years — ${MEASURE_SHORT[state.measure]}. The scale is anchored at zero, so the colour boundary is the line between farming that pays for its capital and farming that does not.`
        : `Each region is shaded by its published rate of return in ${fy(state.year)} — ${MEASURE_SHORT[state.measure]}. The scale is anchored at zero and re-fitted to this year's range.`;
    }
    drawLegend();

    const shown = regions.filter(visible);
    const grey = shown.filter((r) => {
      const c = cellFor(r);
      return c.value == null || c.tier >= 4;
    }).length;
    const weak = shown.filter((r) => cellFor(r).tier === 3 && cellFor(r).value != null).length;
    // In long-run mode there is never a weak average — an average may only take
    // firm-or-fair years — so that clause has to disappear rather than print "0".
    mapNote.innerHTML = `<strong>Read the grey.</strong> ${grey} of the ${shown.length} regions on
      the map carry no figure the survey can support ${state.mode === 'mean' ? 'over the long run' : `in ${esc(fy(state.year))}`}${
      weak > 0
        ? `, and ${weak} more are weak enough that only the direction is safe to read — they are drawn as an outline`
        : ''}.
      The median cell in this survey carries a relative standard error of about
      ${esc(String(Math.round(meta.survey.medianRse ?? 0)))}% ${glossIcon('rse')}, which is why this
      map would rather leave ground blank than shade it a plausible colour.`;

    drawStrip();
    drawFall();
    setParams({
      measure: state.measure,
      mode: state.mode,
      year: state.mode === 'year' ? state.year : null,
      zone: state.zone,
    });
  }

  function drawLegend(): void {
    mapLegend.innerHTML = '';
    const ramp = document.createElement('div');
    ramp.className = 'row';
    ramp.style.flexWrap = 'wrap';
    ramp.style.gap = '2px';
    const steps = [-1, -0.66, -0.33, 0, 0.33, 0.66, 1];
    for (const s of steps) {
      const sw = document.createElement('span');
      sw.className = 'legend-swatch';
      sw.style.width = '30px';
      sw.style.height = '14px';
      sw.style.borderRadius = '2px';
      sw.style.background = returnColour(s * cap, cap, hueFor(state.measure));
      sw.title = `${signedPct(s * cap, 0)}`;
      ramp.append(sw);
    }
    const scaleRow = document.createElement('div');
    scaleRow.className = 'legend';
    scaleRow.innerHTML = `
      <span class="legend-item"><span class="mono">${esc(signedPct(-cap, 0))}</span></span>`;
    scaleRow.append(ramp);
    scaleRow.insertAdjacentHTML('beforeend', `
      <span class="legend-item"><span class="mono">${esc(signedPct(cap, 0))}</span></span>
      <span class="legend-item">${state.measure === 'ex' ? 'teal: what the farming earned' : 'ochre: farming plus what the land added'}; red: below zero</span>`);
    mapLegend.append(scaleRow);

    const evidence = document.createElement('div');
    evidence.className = 'legend';
    evidence.innerHTML = [
      `<span class="legend-item"><span class="legend-swatch" style="border:2px solid ${hueFor(state.measure)};background:none"></span> outline only: weak estimate — direction only</span>`,
      swatch(NEUTRAL, 'grey: no basis for a figure, or no estimate published'),
    ].join('');
    mapLegend.append(evidence);
  }

  sync();
}

/* ══════════════════════════ small helpers ══════════════════════════ */

function swatch(colour: string, label: string): string {
  return `<span class="legend-item"><span class="legend-swatch" style="background:${colour}"></span> ${esc(label)}</span>`;
}

function shorten(s: string, max: number): string {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return `${sp > max * 0.6 ? cut.slice(0, sp) : cut}…`;
}

function clampYear(v: number, years: number[], fallback: number): number {
  if (!Number.isFinite(v)) return fallback;
  return years.includes(v) ? v : fallback;
}

function readZone(v: string | null): Zone | null {
  return v === 'Pastoral' || v === 'Wheat Sheep' || v === 'High Rainfall' ? v : null;
}

interface SegHandle { node: HTMLElement; update: () => void }

function segmented(
  label: string,
  options: Array<{ key: string; label: string }>,
  get: () => string,
  set: (k: string) => void,
): SegHandle {
  const wrap = document.createElement('div');
  wrap.className = 'control';
  // At 375px the two long labels made this control 402px wide, and `.seg` has
  // `overflow: hidden` — so the second option was silently CLIPPED, half its
  // words and half its hit area gone, with no page scrollbar to hint at it.
  wrap.style.flex = '1 1 240px';
  const lab = document.createElement('span');
  lab.className = 'field-label';
  lab.textContent = label;
  const seg = document.createElement('div');
  seg.className = 'seg';
  seg.style.display = 'flex';
  seg.style.maxWidth = '100%';
  seg.setAttribute('role', 'group');
  seg.setAttribute('aria-label', label);
  const buttons: HTMLButtonElement[] = [];
  for (const o of options) {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = o.label;
    b.dataset.key = o.key;
    b.style.whiteSpace = 'normal';
    b.style.flex = '1 1 0';
    b.style.minWidth = '0';
    b.style.lineHeight = '1.25';
    b.style.padding = '6px 10px';
    b.addEventListener('click', () => { if (get() !== o.key) set(o.key); });
    buttons.push(b);
    seg.append(b);
  }
  wrap.append(lab, seg);
  const update = () => {
    for (const b of buttons) b.setAttribute('aria-pressed', String(b.dataset.key === get()));
  };
  update();
  return { node: wrap, update };
}
